import { type FormEvent, useState } from 'react';
import { useApi } from './api';

interface Mensaje {
  rol: 'persona' | 'bot';
  texto: string;
  articulos?: { slug: string; titulo: string }[];
}

/**
 * Conversación con el asistente de soporte (arquitectura 2.12): responde solo con la base de
 * conocimiento y, si no basta, pasa la conversación a un ticket con una persona.
 */
export function Asistente({
  alAbrirArticulo,
  alTicket,
}: {
  alAbrirArticulo: (slug: string) => void;
  alTicket?: (id: string) => void;
}) {
  const api = useApi();
  const [mensajes, setMensajes] = useState<Mensaje[]>([]);
  const [conversacionId, setConversacionId] = useState<string | null>(null);
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ticket, setTicket] = useState<string | null>(null);

  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    const pregunta = texto.trim();
    if (!pregunta) return;
    setTexto('');
    setError(null);
    setMensajes((m) => [...m, { rol: 'persona', texto: pregunta }]);
    setEnviando(true);
    try {
      const r = await api<{
        conversacionId: string;
        respuesta: string;
        articulos: Mensaje['articulos'];
      }>('POST', '/platform/conocimiento/bot', {
        pregunta,
        ...(conversacionId && { conversacionId }),
      });
      setConversacionId(r.conversacionId);
      setMensajes((m) => [...m, { rol: 'bot', texto: r.respuesta, articulos: r.articulos ?? [] }]);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setEnviando(false);
    }
  };
  const aTicket = async () => {
    if (!conversacionId) return;
    try {
      const r = await api<{ ticketId: string; folio?: string }>(
        'POST',
        `/platform/conocimiento/bot/${conversacionId}/ticket`,
      );
      setTicket(r.folio ?? 'creado');
      alTicket?.(r.ticketId);
    } catch (err) {
      setError((err as Error).message);
    }
  };
  return (
    <section className="seccion asistente">
      <header>
        <h2>Pregúntale al asistente</h2>
      </header>
      <div className="cuerpo" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {!mensajes.length && (
          <p className="subtitulo">
            Responde con el Manual y las preguntas frecuentes. No ve los datos de tu negocio.
          </p>
        )}
        {mensajes.map((m, i) => (
          <div
            key={i}
            style={{
              alignSelf: m.rol === 'persona' ? 'flex-end' : 'flex-start',
              maxWidth: '85%',
              padding: '8px 12px',
              borderRadius: 10,
              border: '1px solid var(--borde-suave)',
              background: m.rol === 'persona' ? 'rgba(59,130,246,0.08)' : 'transparent',
            }}
          >
            <div style={{ whiteSpace: 'pre-wrap' }}>{m.texto}</div>
            {m.articulos && m.articulos.length > 0 && (
              <div className="subtitulo" style={{ marginTop: 4 }}>
                Fuente:{' '}
                {m.articulos.map((a, j) => (
                  <span key={a.slug}>
                    {j > 0 && ' · '}
                    <button className="enlace" onClick={() => alAbrirArticulo(a.slug)}>
                      {a.titulo}
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
        {error && <p className="error">{error}</p>}
        {ticket ? (
          <p className="etiqueta exito">Te comunicamos con Soporte (ticket {ticket}).</p>
        ) : (
          conversacionId && (
            <button
              className="boton"
              style={{ alignSelf: 'flex-start' }}
              onClick={() => void aTicket()}
            >
              No se resolvió: hablar con una persona
            </button>
          )
        )}
        <form onSubmit={(e) => void enviar(e)} style={{ display: 'flex', gap: 8 }}>
          <input
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            placeholder="Escribe tu pregunta"
            maxLength={2000}
            style={{ flex: 1 }}
            disabled={enviando}
          />
          <button className="boton primario" disabled={enviando}>
            {enviando ? 'Pensando…' : 'Preguntar'}
          </button>
        </form>
      </div>
    </section>
  );
}
